import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { EquipmentTypeahead } from '~hooks/useEquipment/models';
import { TelematicsVehicle } from '~hooks/useTelematicsVehicles/models';
import useTelematicsVehicles from '~hooks/useTelematicsVehicles/useTelematicsVehicles';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

import { EquipmentSelector } from '../UserManagement/EquipmentSelector';
import { MAPPING_SELECT_WIDTH_PX } from './QuickBooksMapping/mappingColumnWidth';

interface TelematicsVehicleEquipmentSelectorProps {
  onEquipmentSaved: () => void;
  remoteVehicle: TelematicsVehicle;
}

export const TelematicsVehicleEquipmentSelector = observer(
  ({ onEquipmentSaved, remoteVehicle }: TelematicsVehicleEquipmentSelectorProps) => {
    const { userStore, toasterStore } = useStores();
    const { updateTelematicsVehicleById } = useTelematicsVehicles();

    // Get user permissions to determine if user can edit telematics remote vehicles
    const userPermissions = useMemo(() => {
      return userStore.getPermissions();
    }, [userStore.getPermissions()]);

    const saveRemoteVehicleEquipment = async (data: Nullable<EquipmentTypeahead>) => {
      try {
        await updateTelematicsVehicleById(remoteVehicle.id, {
          equipment_id: data?.id ?? null,
        });
      } catch {
        // the hook already toasted and reported the failure
        return false;
      }

      onEquipmentSaved();
      toasterStore.push(
        alert(
          t('administration.integration.vehicle_updated', {
            name:
              remoteVehicle.remoteVehicleExternalName ||
              remoteVehicle.remoteVehicleExternalKey ||
              remoteVehicle.remoteVehicleExternalId,
          }),
          AlertTypes.success,
        ),
      );

      return true;
    };

    return (
      <Box sx={{ width: MAPPING_SELECT_WIDTH_PX }}>
        <EquipmentSelector
          defaultEquipment={remoteVehicle.equipment}
          onSelect={saveRemoteVehicleEquipment}
          disabled={!userPermissions?.canEditTelematicsRemoteVehicle}
          shared={false}
        />
      </Box>
    );
  },
);
